import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { TrendingUp, Calendar, DollarSign, Activity, Bed, Users } from 'lucide-react';
import { getDashboard } from '../services/adminService';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDashboard()
      .then(data => {
        setStats(data);
        setMonthlyRevenue(data.monthlyRevenue || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const pieData = stats
    ? [
        { name: 'Occupied', value: parseFloat(stats.occupancyRate), color: '#3b82f6' },
        { name: 'Available', value: Math.max(0, 100 - parseFloat(stats.occupancyRate)), color: '#64748b' }
      ]
    : [
        { name: 'Occupied', value: 0, color: '#3b82f6' },
        { name: 'Available', value: 100, color: '#64748b' }
      ];

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => (
    <Card className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all duration-200 transform hover:scale-[1.02]">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-400">{title}</p>
            <p className="text-3xl font-bold text-white">{value}</p>
            {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-600/20 border border-${color}-500/30`}>
            <Icon className={`h-6 w-6 text-${color}-400`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400 mt-1">Welcome back! Here's what's happening at your hotel today.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Total Bookings" value={loading ? '...' : (stats?.totalBookings ?? '—')} subtitle="This month" icon={Calendar} color="blue" />
        <StatCard title="Revenue" value={loading ? '...' : (stats ? `৳${stats.totalRevenue.toLocaleString()}` : '—')} subtitle="This month" icon={DollarSign} color="green" />
        <StatCard title="Occupancy Rate" value={loading ? '...' : (stats ? `${stats.occupancyRate}%` : '—')} subtitle="Current" icon={Activity} color="purple" />
        <StatCard title="Available Rooms" value={loading ? '...' : (stats?.availableRooms ?? '—')} subtitle={stats ? `of ${stats.totalRooms} total` : 'Right now'} icon={Bed} color="orange" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-green-400" />
              <span>Monthly Revenue</span>
            </CardTitle>
            <CardDescription className="text-slate-400">Revenue trend over the past 6 months</CardDescription>
          </CardHeader>
          <CardContent>
            {monthlyRevenue.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyRevenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => `৳${v / 1000}k`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
                    formatter={(v) => [`৳${v.toLocaleString()}`, 'Revenue']}
                  />
                  <Bar dataKey="revenue" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-500">No data available</div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center space-x-2">
              <Users className="h-5 w-5 text-blue-400" />
              <span>Booking Trends</span>
            </CardTitle>
            <CardDescription className="text-slate-400">Number of bookings per month</CardDescription>
          </CardHeader>
          <CardContent>
            {monthlyRevenue.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={monthlyRevenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
                    formatter={(v) => [v, 'Bookings']}
                  />
                  <Line type="monotone" dataKey="bookings" stroke="#06b6d4" strokeWidth={3} dot={{ fill: '#06b6d4', strokeWidth: 2, r: 4 }} activeDot={{ r: 6, fill: '#06b6d4' }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-500">No data available</div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Occupancy Overview</CardTitle>
            <CardDescription className="text-slate-400">Current room occupancy status</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => [`${v}%`, 'Occupancy']}
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Recent Activity</CardTitle>
            <CardDescription className="text-slate-400">Latest updates and notifications</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[160px] flex items-center justify-center text-slate-500">
              No recent activity
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
